import { Elevion } from 'lib-shared-elevion';
import { Tag } from 'lib-shared-legacy-types';
import {
  elevionCustomAttributePrefix,
  ElevionNode,
  elevionSystemAttributePrefix,
} from 'lib-shared-types-api';

import { isCustomFieldValueEmpty } from 'utils/customFields/isCustomFieldValueEmpty';
import { CustomField } from 'utils/customFields/useLazyCustomFields/useLazyCustomFields.types';
import { isLeafNode } from 'utils/elevion/elevionUtils';

/**
 * Source-agnostic Elevion render-condition evaluation shared by every sidebar
 * card type (native partner widgets, custom cards, and built-in static cards).
 * This is the single home for the condition engine used by the flat
 * `sidebar-cards-admin-management` model.
 */

/**
 * The runtime context shape consumed by `Elevion.evalJson`. Keys are namespaced
 * Elevion identifiers (e.g. `system__queue`, `custom__order_id`); values are
 * either single strings (for `sfield` comparisons) or string arrays (for
 * `safield` comparisons).
 */
export type ElevionConversationContext = Record<
  string,
  string | readonly string[]
>;

type BuildElevionConversationContextArgs = {
  readonly queueId?: string | null;
  readonly tags?: ReadonlyArray<Pick<Tag, 'id'>> | null;
  readonly conversationCustomFields?: readonly CustomField[];
  readonly contactCustomFields?: readonly CustomField[];
};

/**
 * Builds the flat key/value map that `Elevion.evalJson` evaluates against. The
 * map covers:
 *
 * - `system__queue` — array containing the current queue id (empty when absent).
 * - `system__tags` — array of conversation tag ids (empty when absent).
 * - `custom__<identifier>` — conversation custom attribute values, falling back
 *   to contact custom attribute values when the same identifier isn't set on
 *   the conversation.
 *
 * Conversation custom attributes take precedence over contact ones when the
 * same identifier exists on both entities.
 */
export const buildElevionConversationContext = ({
  queueId,
  tags,
  conversationCustomFields,
  contactCustomFields,
}: BuildElevionConversationContextArgs): ElevionConversationContext => {
  const context: ElevionConversationContext = {};

  context[`${elevionSystemAttributePrefix}queue`] = queueId ? [queueId] : [];
  context[`${elevionSystemAttributePrefix}tags`] =
    tags?.map((tag) => tag.id) ?? [];

  // Contact custom attributes first, conversation last — conversation wins on
  // identifier collision.
  [...(contactCustomFields ?? []), ...(conversationCustomFields ?? [])].forEach(
    ({ attribute, value }) => {
      const key = `${elevionCustomAttributePrefix}${attribute.identifier}`;
      const empty = attribute.data_type === 'string_list' ? [] : '';

      context[key] = isCustomFieldValueEmpty(value)
        ? empty
        : (value?.value ?? empty);
    },
  );

  return context;
};

/**
 * Evaluates the list of render-conditions Elevion AST nodes against the current
 * conversation context and returns whether the item should be shown. Multiple
 * nodes are AND'd together: all must evaluate to true for the item to be shown.
 *
 * Fail-open is list-wide, not per-node: an empty/missing list, or any single
 * node that references an unknown field / throws during evaluation, resolves to
 * "show the item" — even when a sibling node would have evaluated to `false`.
 * The intent is "errors never hide cards": if any part of the tree is
 * unevaluable, don't trust the rest of the AND to make a hiding decision.
 */
export const matchesRenderConditions = (
  conditions: readonly ElevionNode[] | null | undefined,
  context: ElevionConversationContext,
): boolean => {
  if (!conditions || conditions.length === 0) {
    return true;
  }

  for (const node of conditions) {
    if (referencesUnknownField(node, context)) {
      return true;
    }

    try {
      if (!Elevion.evalJson(JSON.stringify(node), context)) {
        return false;
      }
    } catch {
      return true;
    }
  }

  return true;
};

const referencesUnknownField = (
  node: ElevionNode,
  context: ElevionConversationContext,
): boolean => {
  if (isLeafNode(node)) {
    return !(node.field in context);
  }

  return node.nodes.some((child) => referencesUnknownField(child, context));
};
